import ModalBase from 'components/Luxkit/Modal/ModalBase'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import ModalContent from 'components/Luxkit/Modal/ModalContent'
import ModalFooter from 'components/Luxkit/Modal/ModalFooter'
import ModalHeader from 'components/Luxkit/Modal/ModalHeader'
import useStack from 'hooks/useStack'
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import TourV2DeparturesMonthsCalendar from './TourV2DeparturesMonthsCalendar'
import OfferCalendarCapacityChip from 'components/OfferPage/Filters/CalendarPicker/CalendarCapacity/OfferCalendarCapacityChip'
import TourV2DepartureTileList from './TourV2DepartureTileList'
import OfferCapacityWithAgesSelection from 'components/OfferPage/Filters/CapacityWithAges/OfferCapacityWithAgesSelection'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { initialiseCheckout } from 'actions/CheckoutActions'
import { navigateToCheckout } from 'checkout/actions/navigateActions'
import generateTourV2CheckoutItem from 'lib/tours/generateTourV2CheckoutItems'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import Group from 'components/utils/Group'
import OfferCalendarLuxPlusToggle from 'luxPlus/components/OfferCalendarLuxPlusToggle'
import { pluralizeToString } from 'lib/string/pluralize'
import { getMaxGuestCount, sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { areDeparturesSoldOut, getDepartureCheapestOptionForTravellers, getDepartureOptionsForTravellers } from 'lib/tours/tourDepartureUtils'
import { TOUR_V2_DEFAULT_OCCUPANCIES } from 'constants/tours'
import { useAppDispatch } from 'hooks/reduxHooks'
import MessageBanner from 'components/Luxkit/Banners/MessageBanner'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { addToCartGTM, addTourToCart } from 'analytics/eventDefinitions'
import { addToCart } from 'analytics/snowplow/events'
import { fireEcommerceGTMEvent, addGTMEvent } from 'api/googleTagManager'
import * as Analytics from 'analytics/analytics'
import { sum } from 'lib/array/arrayUtils'
import GeoContext from 'contexts/geoContext'
import { getFilteredDepartures } from 'selectors/TourV2/tourV2OfferPageSelectors'
import TourV2DeparturesSoldOutTile from './TourV2DeparturesSoldOutTile'

type Step = 'travellers' | 'departure' | 'month'

interface Props {
  offer: App.Tours.TourV2Offer;
  travellers?: Array<App.Occupants>;
  defaultDate?: Date;
  defaultDeparture?: App.Tours.TourV2OfferDeparture;
  defaultStep?: Step;
  filteredDepartures: Array<App.Tours.TourV2OfferDeparture>;
  variation: App.Tours.TourV2OfferVariation;
}

function TourV2DepartureBookingModal(props: Props) {
  const {
    offer,
    travellers = TOUR_V2_DEFAULT_OCCUPANCIES,
    defaultDate,
    defaultDeparture,
    defaultStep,
    variation,
    filteredDepartures,
  } = props

  const allDepartures = variation.departures
  const { currentCurrency } = useContext(GeoContext)
  const calcDefaultStep: Step = travellers === TOUR_V2_DEFAULT_OCCUPANCIES ? 'travellers' : 'departure'
  const [currentStep, goBack, nextStep] = useStack<Step>(defaultStep ?? calcDefaultStep)
  // Used to default the departures page to the correct scroll position
  const [defaultDepartureDate, setDefaultDepartureDate] = useState<Date | undefined>(defaultDate)
  const [selectedDeparture, setSelectedDeparture] = useState<App.Tours.TourV2OfferDeparture | undefined>(defaultDeparture)

  const offerPageDispatch = useOfferPageDispatch()
  const dispatch = useAppDispatch()
  const { resolve } = useModalElementContext()

  useEffect(() => {
    if (!currentStep) {
      // we 'popped' to the end of the list...means we close
      resolve()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep])

  const checkoutDeparture = useCallback((departure: App.Tours.TourV2OfferDeparture) => {
    const items = travellers.map(traveller => {
      const option = getDepartureCheapestOptionForTravellers(departure, traveller)
      if (option) {
        return generateTourV2CheckoutItem(
          offer,
          departure,
          traveller,
          option,
        )
      }
    })

    if (items.every(item => !!item)) {
      dispatch(initialiseCheckout(items))
      dispatch(navigateToCheckout())

      const totalTravellers = sumUpOccupancies(travellers)
      const priceTotal = sum(items, item => item.total ?? 0)
      fireEcommerceGTMEvent(addToCartGTM(offer, currentCurrency, priceTotal, totalTravellers, 'tour'))
      addGTMEvent(addTourToCart(offer, currentCurrency, priceTotal, totalTravellers))
      Analytics.trackEvent(addToCart())

      resolve()
    }
  }, [currentCurrency, dispatch, offer, resolve, travellers])

  const onTravellersSubmit = useCallback<React.FormEventHandler<HTMLFormElement>>(e => {
    e.preventDefault()
    if (travellers === TOUR_V2_DEFAULT_OCCUPANCIES) {
      offerPageDispatch({ type: OfferPageActions.setRooms, rooms: [...TOUR_V2_DEFAULT_OCCUPANCIES] as any })
    }
    if (selectedDeparture) {
      // previously selected a departure, means the user was validating travellers
      const option = getDepartureOptionsForTravellers(selectedDeparture, travellers)
      if (option.length) {
        checkoutDeparture(selectedDeparture)
      } else {
        // not valid, send them back to departure step to pick another one
        nextStep('departure')
      }
    } else {
      nextStep('departure')
    }
  }, [checkoutDeparture, nextStep, offerPageDispatch, selectedDeparture, travellers])

  const onCapacityFilterSelect = useCallback(() => {
    nextStep('travellers')
  }, [nextStep])

  const onYearSelect = useCallback(() => {
    nextStep('month')
  }, [nextStep])

  const onDepartureSelect = useCallback((departure: App.Tours.TourV2OfferDeparture) => {
    if (travellers === TOUR_V2_DEFAULT_OCCUPANCIES) {
      // this means the user hasn't explicitly picked their travellers yet
      // so force them to consider it by taking them there
      setSelectedDeparture(departure)
      setDefaultDepartureDate(new Date(departure.startDate))
      nextStep('travellers')
      return
    }

    const options = getDepartureOptionsForTravellers(departure, travellers)
    if (options.length) {
      checkoutDeparture(departure)
    }
    // somehow selected a departure not for sale?
  }, [checkoutDeparture, nextStep, travellers])

  const onMonthSelect = useCallback((selectedDate: Date) => {
    setDefaultDepartureDate(selectedDate)
    nextStep('departure')
  }, [nextStep])

  const maxCapacityLabel = useMemo(() => {
    const maxTravellers = getMaxGuestCount(offer.roomCapacity)
    return `Maximum capacity per room is ${pluralizeToString('traveller', maxTravellers)}`
  }, [offer.roomCapacity])

  const isInvalidDeparture = useMemo(() => {
    // We want to let the user know when they select a departure, then fills in their
    // travellers, but the departure they selected is not available for their travellers
    return selectedDeparture && getDepartureOptionsForTravellers(selectedDeparture, travellers).length > 0
  }, [selectedDeparture, travellers])

  const soldOutReason = useMemo(() => areDeparturesSoldOut(filteredDepartures, travellers), [filteredDepartures, travellers])

  return <ModalBase mode="drawer">
    {currentStep === 'travellers' && <form onSubmit={onTravellersSubmit}>
      <ModalHeader
        title="Travellers"
        subtitle={maxCapacityLabel}
        onBackButtonClick={goBack}
      />
      <ModalBody>
        <ModalContent>
          <OfferCapacityWithAgesSelection
            capacities={offer.roomCapacity}
            maxRooms={5}
          />
        </ModalContent>
      </ModalBody>
      <ModalFooter
        primaryActionProps={{
          children: 'Continue',
          type: 'submit',
        }}
      />
    </form>}
    {currentStep === 'departure' && <>
      <ModalHeader title="Select departure" onBackButtonClick={goBack}>
        <OfferCalendarCapacityChip
          onClick={onCapacityFilterSelect}
          offer={offer}
        />
      </ModalHeader>
      <ModalBody>
        {isInvalidDeparture && <MessageBanner
          kind="warning"
          description="Your selected departure is sold out for single rooms. Please select an alternate date."
          density="compact"
        />}
        {!!soldOutReason && <TourV2DeparturesSoldOutTile
          reason={soldOutReason}
          onUpdateTravelers={onCapacityFilterSelect}
        />}
        {!soldOutReason && <TourV2DepartureTileList
          offer={offer}
          onYearSelect={onYearSelect}
          onDepartureSelect={onDepartureSelect}
          defaultDate={defaultDepartureDate}
          departures={allDepartures}
          travellers={travellers}
          variation={variation}
        />}
      </ModalBody>
    </>}
    {currentStep === 'month' && <>
      <ModalHeader title="Select a month" onBackButtonClick={goBack}>
        <Group direction="horizontal" gap={8} wrap="wrap">
          <OfferCalendarCapacityChip
            onClick={onCapacityFilterSelect}
            offer={offer}
          />
          {offer.luxPlus.hasMemberPrices && <OfferCalendarLuxPlusToggle />}
        </Group>
      </ModalHeader>
      <ModalBody>
        <ModalContent>
          {soldOutReason && <TourV2DeparturesSoldOutTile
            reason={soldOutReason}
            onUpdateTravelers={onCapacityFilterSelect}
          />}
          {!soldOutReason && <TourV2DeparturesMonthsCalendar
            onSelect={onMonthSelect}
            offer={offer}
            travellers={travellers}
            departures={filteredDepartures}
          />}
        </ModalContent>
      </ModalBody>
    </>}
  </ModalBase>
}

type MappedProps = Pick<Props, 'travellers' | 'filteredDepartures' | 'variation'>

export default withOfferPageState((state): MappedProps => ({
  travellers: state.rooms,
  filteredDepartures: getFilteredDepartures(state),
  variation: state.tourV2Variation!,
}))(TourV2DepartureBookingModal)
